// ─────────────────────────────────────────────────────────────────────────────
// Reports GA4 analytics helper
//
// Thin wrapper around window.gtag / dataLayer. If GA is not configured on the
// page (dev, native shell, tests) every call is a safe no-op — it never throws.
//
// Every reports event carries 5 mandatory parameters (location_id, outlet_id,
// user_id, user_role, platform) merged with the event-specific params. Callers
// pass the base once via `makeReportTracker(base)` and then fire named events.
// ─────────────────────────────────────────────────────────────────────────────

export type GtagParams = Record<string, string | number | boolean | null | undefined>;

export interface ReportEventBase {
    location_id?: string;
    /** Defaults to location_id when omitted (single-location merchants). */
    outlet_id?: string;
    user_id?: string;
    user_role?: string;
    platform?: string;
}

function resolvePlatform(explicit?: string): string {
    if (explicit) return explicit;
    try {
        const cap = (window as any)?.Capacitor;
        if (cap?.isNativePlatform?.()) return 'native';
    } catch {
        /* ignore */
    }
    return 'web';
}

/** The 5 mandatory parameters attached to every reports event. */
function mandatoryParams(base: ReportEventBase): GtagParams {
    return {
        location_id: base.location_id ?? '',
        outlet_id: base.outlet_id ?? base.location_id ?? '',
        user_id: base.user_id ?? '',
        user_role: base.user_role ?? '',
        platform: resolvePlatform(base.platform),
    };
}

/** Fire a single GA4 event. Safe no-op when analytics is not present. */
export function trackReportEvent(eventName: string, params: GtagParams, base: ReportEventBase): void {
    try {
        const payload: GtagParams = { ...mandatoryParams(base), ...params };
        const w = window as any;
        if (typeof w?.gtag === 'function') {
            w.gtag('event', eventName, payload);
        } else if (Array.isArray(w?.dataLayer)) {
            w.dataLayer.push({ event: eventName, ...payload });
        }
        // Otherwise analytics is not configured — silently ignore.
    } catch {
        // Analytics must never break the UI.
    }
}

/** Bind a base context once and return ergonomic event helpers for a screen. */
export function makeReportTracker(base: ReportEventBase) {
    return {
        /** maghil_reports_filter — search / dropdown filters applied to a table. */
        filter(params: { report_name: string; filter_type: string; filter_value: string; table_name: string }) {
            trackReportEvent('maghil_reports_filter', params, base);
        },
        /** maghil_reports_table_config — page size / column config changes. */
        tableConfig(params: { report_name: string; table_name: string; config_type: string; config_value: string }) {
            trackReportEvent('maghil_reports_table_config', params, base);
        },
        /** maghil_reports_pin — pin / unpin a row. */
        pin(params: { report_name: string; table_name: string; action: 'pin' | 'unpin'; item_name: string; item_rank: number | string }) {
            trackReportEvent('maghil_reports_pin', params, base);
        },
        /** maghil_reports_pin_clear — clear all pins. */
        pinClear(params: { report_name: string; table_name: string; cleared_count: number }) {
            trackReportEvent('maghil_reports_pin_clear', params, base);
        },
        /** maghil_reports_sort — column / option sorting applied to a table. */
        sort(params: { report_name: string; table_name: string; sort_column: string; sort_direction: string }) {
            trackReportEvent('maghil_reports_sort', params, base);
        },
    };
}

export type ReportTracker = ReturnType<typeof makeReportTracker>;
